import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Landmark, Upload, FileSpreadsheet, Search, X, Link2, Trash2, History,
  CheckCircle2, AlertCircle, Filter, Wand2, Loader2, Info, Unlink, ArrowLeftRight, Undo2,
} from "lucide-react";
import { BranchFilter } from "@/components/BranchFilter";
import { PaginationControls } from "@/components/PaginationControls";
import {
  ExceptionRequestDialog, ExceptionsTab, ReconSegregationStrip, EXC_CHIP,
} from "@/components/BankReconExceptions";

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

export default function BankReconPage() {
  const [tab, setTab] = useState("transactions"); // 'transactions' | 'uploads' | 'balances'
  const [txns, setTxns] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [batches, setBatches] = useState([]);
  const [banks, setBanks] = useState([]);
  const [balances, setBalances] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [filter, setFilter] = useState("ALL"); // ALL | MATCHED | UNMATCHED
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("");
  const [filterBank, setFilterBank] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [assigning, setAssigning] = useState(null); // txn id
  const [uploadOpen, setUploadOpen] = useState(false);
  // 2026-Feb — Auto Reconcile (batch-in-description matcher). `null`
  // means the modal is closed; otherwise holds the dry-run response.
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoPreview, setAutoPreview] = useState(null);
  // 2026-Feb — Bulk unlink by batch (companion tool for Auto Reconcile).
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  // Jul-2026 — Bank Recon Exception Approval workflow.
  const [exceptionSummary, setExceptionSummary] = useState(null);
  const [raisingException, setRaisingException] = useState(null); // txn
  // Jul-2026 — Reversal Match (DR ↔ CR offset for bounced payments).
  const [reversalPair, setReversalPair] = useState(null); // { dr, cr }
  const [suggestOpen, setSuggestOpen] = useState(false);

  // Pagination — Transactions and Uploads tabs only (Balances has one row
  // per bank ledger so doesn't need it). Client-side because the existing
  // filters (search / branch / date range / matched-state) run on the
  // already-loaded `txns` array; paginating server-side would require a
  // bigger refactor of the API layer for no user-visible benefit.
  const [txnPage, setTxnPage] = useState(1);
  const [txnPageSize, setTxnPageSize] = useState(200);
  const [uploadsPage, setUploadsPage] = useState(1);
  const [uploadsPageSize, setUploadsPageSize] = useState(200);

  function load() {
    loadAllTxns();
    api.get("/bank-recon/uploads").then(r => setUploads(r.data || []));
    api.get("/batch-payments").then(r => setBatches(r.data || []));
    api.get("/bank-ledgers", { params: { active_only: true } }).then(r => setBanks(r.data || []));
    api.get("/bank-recon/bank-balances").then(r => setBalances(r.data || []));
    api.get("/bank-recon/exceptions/summary").then(r => setExceptionSummary(r.data)).catch(() => {});
    setSelected(new Set());
  }

  // Aug-2026 — the backend used to cap this list at 2000 rows, hiding older
  // statement lines. Now fetches ALL transactions in 5000-row chunks
  // (progressively rendered) so client-side filters/search see everything.
  async function loadAllTxns() {
    const PAGE = 5000;
    let all = [], skip = 0;
    for (;;) {
      let r;
      try { r = await api.get("/bank-recon/transactions", { params: { skip, limit: PAGE } }); }
      catch { break; }
      const rows = r.data || [];
      all = all.concat(rows);
      setTxns(all.slice());
      const total = parseInt(r.headers?.["x-total-count"] || "0", 10);
      skip += rows.length;
      if (rows.length < PAGE || (total && skip >= total)) break;
    }
  }
  useEffect(() => { load(); }, []);

  const filteredTxns = useMemo(() => {
    let rows = txns;
    if (filterBranch) rows = rows.filter(t => {
      // Sprint 4.10 — Permissive single-branch match. Historical bank
      // transactions don't carry branch_id (the upload flow predates it).
      // Treat orphan rows (empty/missing branch_id) as belonging to ANY
      // selected branch so they remain visible to the operator — the
      // backend's user_branch_filter still prevents cross-branch leaks.
      const tid = t.branch_id || (t.bank_ledger && t.bank_ledger.branch_id) || "";
      return tid === "" || tid === filterBranch;
    });
    if (filterBank) rows = rows.filter(t => {
      // Legacy rows may lack bank_ledger_id — fall back to their upload's bank.
      const bid = t.bank_ledger_id || (uploads.find(u => u.id === t.upload_id)?.bank_ledger_id) || "";
      return bid === filterBank;
    });
    if (filter === "MATCHED") rows = rows.filter(t => t.linked_batch_id || t.exception_status === "Linked");
    else if (filter === "UNMATCHED") rows = rows.filter(t => !t.linked_batch_id && !(t.exception_status || "") && !t.reversal_group_id);
    else if (filter === "OFFSET") rows = rows.filter(t => t.reversal_group_id);
    else if (filter === "EXC_PENDING") rows = rows.filter(t => t.exception_status === "Pending");
    else if (filter === "EXC_APPROVED") rows = rows.filter(t => t.exception_status === "Approved");
    if (fromDate) rows = rows.filter(t => (t.value_date || "") >= fromDate);
    if (toDate) rows = rows.filter(t => (t.value_date || "") <= toDate);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(t =>
        (t.description || "").toLowerCase().includes(q) ||
        (t.transaction_id || "").toLowerCase().includes(q) ||
        (t.cheque_no || "").toLowerCase().includes(q) ||
        (t.linked_batch_number || "").toLowerCase().includes(q));
    }
    return rows;
  }, [txns, uploads, filter, search, fromDate, toDate, filterBranch, filterBank]);

  // Reset to page 1 whenever the filtered set changes shape — prevents
  // users from being stranded on an empty page after narrowing filters.
  useEffect(() => { setTxnPage(1); }, [filter, search, fromDate, toDate, filterBranch, filterBank]);
  useEffect(() => { setUploadsPage(1); }, [uploads.length]);

  // Visible slice for the Transactions tab.
  const pagedTxns = useMemo(() => {
    const start = (txnPage - 1) * txnPageSize;
    return filteredTxns.slice(start, start + txnPageSize);
  }, [filteredTxns, txnPage, txnPageSize]);

  // Visible slice for the Uploads tab.
  const pagedUploads = useMemo(() => {
    const start = (uploadsPage - 1) * uploadsPageSize;
    return uploads.slice(start, start + uploadsPageSize);
  }, [uploads, uploadsPage, uploadsPageSize]);

  const summary = useMemo(() => {
    const matched = txns.filter(t => t.linked_batch_id).length;
    const offset = txns.filter(t => t.reversal_group_id).length;
    const unmatched = txns.length - matched - offset;
    const totalDr = txns.filter(t => t.cr_dr === "DR").reduce((s, t) => s + Number(t.amount || 0), 0);
    return { matched, unmatched, offset, totalDr };
  }, [txns]);

  // 2026-Feb — Roll up currently-linked bank txns per batch so the
  // Bulk Unlink picker only shows batches with actual data attached
  // (rather than every batch in the master list). Kept client-side to
  // avoid an extra endpoint — the `txns` array is already in memory.
  const linkedBatchSummary = useMemo(() => {
    const roll = new Map();
    for (const t of txns) {
      if (!t.linked_batch_id) continue;
      const cur = roll.get(t.linked_batch_id) || {
        batch_id: t.linked_batch_id,
        batch_number: t.linked_batch_number || "",
        count: 0,
        total_amount: 0.0,
      };
      cur.count += 1;
      cur.total_amount += Number(t.amount || 0);
      roll.set(t.linked_batch_id, cur);
    }
    return Array.from(roll.values()).sort(
      (a, b) => (a.batch_number || "").localeCompare(b.batch_number || ""),
    );
  }, [txns]);

  async function assign(tid, batchId) {
    try {
      await api.post(`/bank-recon/transactions/${tid}/assign-batch`, { batch_id: batchId });
      toast.success("Linked to batch");
      setAssigning(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Linking failed");
    }
  }
  async function unassign(tid) {
    if (!window.confirm("Unlink this transaction from its batch?")) return;
    try {
      await api.post(`/bank-recon/transactions/${tid}/unassign`);
      toast.success("Unlinked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    }
  }
  async function bulkAssign(batchId) {
    const ids = Array.from(selected);
    try {
      const r = await api.post("/bank-recon/transactions/bulk-assign", { transaction_ids: ids, batch_id: batchId });
      toast.success(`Linked ${r.data.linked_count} transaction(s) to ${r.data.batch_number}`);
      setBulkAssignOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk linking failed");
    }
  }

  // 2026-Feb — Auto Reconcile flow. Two-step: (1) dry-run to compute
  // matched / ambiguous / no-batch buckets and render a preview; (2)
  // apply to actually write the linkages. Matching is done server-side
  // (see `POST /api/bank-recon/auto-reconcile` in server.py) — this
  // function is purely a thin driver + preview modal.
  async function runAutoDryRun() {
    setAutoBusy(true);
    setAutoOpen(true);
    setAutoPreview(null);
    try {
      const r = await api.post("/bank-recon/auto-reconcile", { dry_run: true });
      setAutoPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-reconcile preview failed");
      setAutoOpen(false);
    } finally {
      setAutoBusy(false);
    }
  }
  async function applyAutoReconcile() {
    if (!autoPreview) return;
    const n = autoPreview.matched || 0;
    if (n === 0) { toast.info("Nothing to apply — no matches found."); return; }
    if (!window.confirm(`Auto-link ${n} bank transaction(s) to their batches based on batch numbers found in the description? This is audit-logged but cannot be undone in bulk (each row can be individually unlinked later).`)) return;
    setAutoBusy(true);
    try {
      const r = await api.post("/bank-recon/auto-reconcile", { dry_run: false });
      toast.success(`Auto-linked ${r.data.matched} bank transaction(s) across ${Object.keys(r.data.applied || {}).length} batch(es).`);
      setAutoOpen(false);
      setAutoPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-reconcile apply failed");
    } finally {
      setAutoBusy(false);
    }
  }

  // 2026-Feb — Bulk unlink by batch. Operator picks a batch from the
  // dropdown (populated from `linkedBatchSummary` — only batches that
  // actually have linked bank txns, so the dropdown stays short). One
  // API call unlinks every bank txn currently pointing at that batch.
  async function bulkUnassignByBatch(batchId) {
    const b = batches.find(x => x.id === batchId);
    const label = b?.batch_number || batchId;
    const count = linkedBatchSummary.find(x => x.batch_id === batchId)?.count || 0;
    if (count === 0) { toast.info(`No transactions linked to ${label}.`); return; }
    if (!window.confirm(`Unlink all ${count} bank transaction(s) currently linked to ${label}? Each row will drop back to Unmatched — you can re-link them via Auto Reconcile or manually. This is audit-logged.`)) return;
    try {
      const r = await api.post("/bank-recon/bulk-unassign-by-batch", { batch_id: batchId });
      toast.success(`Unlinked ${r.data.unlinked_count} transaction(s) from ${r.data.batch_number}.`);
      setUnlinkOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk unlink failed");
    }
  }
  function toggleRow(tid) {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(tid)) n.delete(tid); else n.add(tid);
      return n;
    });
  }
  function toggleAllVisible() {
    const visibleIds = filteredTxns.filter(t => (t.cr_dr || "").toUpperCase() !== "CR").map(t => t.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
    setSelected(prev => {
      const n = new Set(prev);
      if (allSelected) visibleIds.forEach(id => n.delete(id));
      else visibleIds.forEach(id => n.add(id));
      return n;
    });
  }

  async function matchReversal(drId, crId, remark) {
    try {
      const r = await api.post("/bank-recon/reversal-match", { dr_txn_id: drId, cr_txn_id: crId, remark });
      toast.success(r.data.released_batch_number
        ? `Reversal matched — DR released from batch ${r.data.released_batch_number}`
        : "Reversal matched — DR and CR now offset each other");
      setReversalPair(null);
      setSelected(new Set());
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal match failed");
      return false;
    }
  }
  async function undoReversal(tid) {
    if (!window.confirm("Undo this DR ↔ CR offset? Both transactions return to Unmatched.")) return;
    try {
      await api.post("/bank-recon/reversal-unmatch", { txn_id: tid });
      toast.success("Offset undone");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    }
  }

  // Jul-2026 — selection analysis for the Reversal Match bulk action.
  const selectedTxns = Array.from(selected).map(id => txns.find(t => t.id === id)).filter(Boolean);
  const selDR = selectedTxns.filter(t => (t.cr_dr || "").toUpperCase() !== "CR");
  const selCR = selectedTxns.filter(t => (t.cr_dr || "").toUpperCase() === "CR");
  const reversalReady = selDR.length === 1 && selCR.length === 1
    && Math.abs(Number(selDR[0].amount || 0) - Number(selCR[0].amount || 0)) < 0.005;

  return (
    <div className="px-4 sm:px-8 py-6 max-w-[1700px] mx-auto">
      <Toaster richColors />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Landmark size={24} className="text-[#F97316]"/> Bank Reconciliation</h1>
          <div className="text-xs text-slate-500 mt-1">Upload bank statements, link each transaction to an ERP batch, and reconcile totals against the Post Payment ledger.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runAutoDryRun} data-testid="br-auto-reconcile-btn"
            className="inline-flex items-center gap-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-4 py-2 text-xs font-bold uppercase tracking-wider">
            <Wand2 size={14}/> Auto Reconcile
          </button>
          <button onClick={() => setSuggestOpen(true)} data-testid="br-suggest-reversals-btn"
            title="Find same-amount DR ↔ CR pairs (bounced payments credited back by the bank)"
            className="inline-flex items-center gap-2 border border-sky-600 text-sky-700 hover:bg-sky-50 px-4 py-2 text-xs font-bold uppercase tracking-wider">
            <ArrowLeftRight size={14}/> Suggest Reversals
          </button>
          <button onClick={() => setUnlinkOpen(true)} data-testid="br-bulk-unlink-btn"
            disabled={linkedBatchSummary.length === 0}
            title={linkedBatchSummary.length === 0 ? "No batches currently have linked bank transactions" : "Unlink all bank transactions attached to a chosen batch"}
            className="inline-flex items-center gap-2 border border-rose-600 text-rose-700 hover:bg-rose-50 px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed">
            <Unlink size={14}/> Bulk Unlink by Batch
          </button>
          <button onClick={() => setUploadOpen(true)} data-testid="br-upload-open-btn"
            className="inline-flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C]">
            <Upload size={14}/> Upload Bank Statement
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-3">
        <KPI label="Total Transactions" value={txns.length} text/>
        <KPI label="Matched" value={summary.matched} text accent={"emerald"}/>
        <KPI label="Unmatched" value={summary.unmatched} text accent={"amber"}/>
        <KPI label="Total Debits" value={summary.totalDr}/>
      </div>
      {/* Jul-2026 — segregated recon summary: ERP-reconciled vs approved
          exceptions are NEVER merged into one undifferentiated figure. */}
      <ReconSegregationStrip summary={exceptionSummary} />

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[["transactions", `Transactions (${txns.length})`], ["exceptions", "Exceptions"], ["balances", `Bank Balances`], ["uploads", `Uploads (${uploads.length})`]].map(([k, l]) => (
          <button key={k} data-testid={`br-tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px ${
              tab === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{l}</button>
        ))}
      </div>

      {tab === "transactions" && (<>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={search} onChange={e => setSearch(e.target.value)} data-testid="br-search"
              placeholder="Search description / txn id / cheque…"
              className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
            {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
          </div>
          <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="br-filter-branch" />
          <select value={filterBank} onChange={e => setFilterBank(e.target.value)} data-testid="br-filter-bank"
            className="text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none bg-white max-w-[220px]">
            <option value="">All Banks</option>
            {banks.map(b => <option key={b.id} value={b.id}>{b.bank_name} — ****{(b.account_number || "").slice(-4)}</option>)}
          </select>
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">From</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid="br-from-date"
              className="text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none bg-white"/>
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 ml-1">To</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} data-testid="br-to-date"
              className="text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none bg-white"/>
            {(fromDate || toDate) && (
              <button onClick={() => { setFromDate(""); setToDate(""); }} data-testid="br-date-clear"
                title="Clear dates" className="text-slate-400 hover:text-slate-700 ml-1"><X size={13}/></button>
            )}
          </div>
          <div className="inline-flex border border-slate-300 bg-white">
            {[["ALL", "All"], ["UNMATCHED", "Unmatched"], ["MATCHED", "Matched"], ["OFFSET", "Offset"], ["EXC_PENDING", "Exc. Pending"], ["EXC_APPROVED", "Exc. Approved"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`br-filter-${v.toLowerCase()}`}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider ${filter === v ? "bg-[#F97316] text-white" : "text-slate-700 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <button onClick={runAutoDryRun} data-testid="br-auto-reconcile-btn-inline"
            title="Automatically link bank transactions to their batches by finding the batch number inside the description"
            className="inline-flex items-center gap-1.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider">
            <Wand2 size={12}/> Auto Reconcile
          </button>
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 ml-auto">
            {filteredTxns.length} txn{filteredTxns.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="border border-slate-200 bg-white overflow-x-auto">
          {selected.size > 0 && (
            <div className="bg-orange-50 border-b border-orange-200 px-4 py-2 flex items-center justify-between" data-testid="br-bulk-bar">
              <div className="text-xs text-orange-900 font-bold">
                {selected.size} transaction(s) selected
                {selCR.length > 0 && <span className="font-normal text-orange-700"> · {selDR.length} DR + {selCR.length} CR</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSelected(new Set())} className="text-[10px] uppercase font-bold tracking-wider text-slate-600 hover:text-slate-900">Clear</button>
                {selDR.length === 1 && selCR.length === 1 && (
                  <button onClick={() => reversalReady && setReversalPair({ dr: selDR[0], cr: selCR[0] })}
                    disabled={!reversalReady}
                    title={reversalReady ? "Offset this bounced DR against its refund CR" : "Amounts must match exactly to form a reversal pair"}
                    data-testid="br-reversal-match-btn"
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider bg-sky-600 text-white px-3 py-1 hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ArrowLeftRight size={11}/> Match Reversal
                  </button>
                )}
                {selCR.length === 0 && (
                  <button onClick={() => setBulkAssignOpen(true)} data-testid="br-bulk-link-btn"
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider bg-[#F97316] text-white px-3 py-1 hover:bg-[#EA580C]">
                    <Link2 size={11}/> Link selected to one Batch
                  </button>
                )}
              </div>
            </div>
          )}
          {/* 2026-Feb — Table now uses `table-fixed` with explicit column
              widths so the Description column can actually truncate.
              Previously `max-w-md` on <td> was ignored because auto-
              layout tables size columns to content — a populated
              `Linked Batch` (e.g. BATCH/0017/26-27) pushed the row
              beyond the viewport and forced horizontal scroll. */}
          <table className="w-full text-xs table-fixed">
            <colgroup>
              <col className="w-10" /><col className="w-24" /><col className="w-32" /><col className="w-20" /><col /><col className="w-14" /><col className="w-28" /><col className="w-40" /><col className="w-28" />
            </colgroup>
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>
                <th className="py-3 px-3">
                  <input type="checkbox" data-testid="br-select-all"
                    title="Select all linkable (DR) transactions"
                    checked={(() => { const linkable = filteredTxns.filter(t => (t.cr_dr || "").toUpperCase() !== "CR"); return linkable.length > 0 && linkable.every(t => selected.has(t.id)); })()}
                    onChange={toggleAllVisible}/>
                </th>
                {["Value Date", "Bank Txn ID", "Cheque", "Description", "Type", "Amount", "Linked Batch", ""].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3 leading-tight">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {filteredTxns.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-slate-400">No transactions. Upload a bank statement to get started.</td></tr>}
              {pagedTxns.map(t => {
                const isCR = (t.cr_dr || "").toUpperCase() === "CR";
                return (
                <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${selected.has(t.id) ? "bg-orange-50/40" : ""} ${isCR ? "bg-emerald-50/20" : ""}`} data-testid={`br-txn-row-${t.id}`}>
                  <td className="py-2 px-3">
                    <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleRow(t.id)}
                      title={isCR ? "Select together with a same-amount DR to match a bank reversal" : ""}
                      data-testid={`br-row-check-${t.id}`}/>
                  </td>
                  <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{fmtDate(t.value_date)}</td>
                  <td className="py-2 px-3 text-slate-500 tabular text-[11px] truncate" title={t.transaction_id || ""}>{t.transaction_id || "—"}</td>
                  <td className="py-2 px-3 text-slate-500 truncate" title={t.cheque_no || ""}>{t.cheque_no && t.cheque_no !== "-" ? t.cheque_no : "—"}</td>
                  <td className="py-2 px-3 text-slate-700 truncate" title={t.description}>{t.description}</td>
                  <td className="py-2 px-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${t.cr_dr === "DR" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{t.cr_dr || "—"}</span>
                  </td>
                  <td className={`py-2 px-3 text-right tabular font-bold whitespace-nowrap ${t.cr_dr === "DR" ? "text-rose-700" : "text-emerald-700"}`}>{formatINR(t.amount)}</td>
                  <td className="py-2 px-3">
                    {t.reversal_group_id
                      ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 border border-sky-300 bg-sky-50 text-sky-800 whitespace-nowrap"
                          title={`DR ↔ CR reversal offset · ${t.reversal_remark || ""}${t.reversal_released_batch_number ? ` · released from ${t.reversal_released_batch_number}` : ""}`}
                          data-testid={`br-offset-chip-${t.id}`}>
                          <ArrowLeftRight size={10}/> OFFSET
                        </span>
                      : t.linked_batch_number
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 whitespace-nowrap truncate" title={t.linked_batch_number}><Link2 size={11} className="shrink-0"/> <span className="truncate">{t.linked_batch_number}</span></span>
                      : (EXC_CHIP[t.exception_status]
                          ? <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 border whitespace-nowrap ${EXC_CHIP[t.exception_status].cls}`}
                              title={`${EXC_CHIP[t.exception_status].label} · ${t.exception_request_number || ""}`}
                              data-testid={`br-exc-chip-${t.id}`}>
                              {t.exception_request_number || EXC_CHIP[t.exception_status].label}
                            </span>
                          : <span className="text-[11px] text-slate-400">— not linked —</span>)}
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    {t.reversal_group_id
                      ? <button onClick={() => undoReversal(t.id)} data-testid={`br-undo-offset-${t.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-sky-700 hover:text-sky-900">
                          <Undo2 size={10}/> Undo Offset
                        </button>
                      : t.linked_batch_id
                      ? <button onClick={() => unassign(t.id)} data-testid={`br-unlink-${t.id}`}
                          className="text-[10px] uppercase font-bold tracking-wider text-rose-600 hover:text-rose-800">Unlink</button>
                      : (EXC_CHIP[t.exception_status]
                          ? <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 italic"
                              title="Under a Reconciliation Exception — manage from the Exceptions tab">Exception</span>
                          : <span className="inline-flex items-center gap-1.5">
                              {isCR
                                ? null
                                : <button onClick={() => setAssigning(t)} data-testid={`br-link-${t.id}`}
                                    className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider bg-[#F97316] text-white px-2 py-1 hover:bg-[#EA580C]">
                                    <Link2 size={10}/> Link Batch
                                  </button>}
                              <button onClick={() => setRaisingException(t)} data-testid={`br-exception-${t.id}`}
                                title="Mark for Exception Approval — this bank transaction has no ERP entry"
                                className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider border border-purple-500 text-purple-700 px-2 py-1 hover:bg-purple-50">
                                Exception
                              </button>
                            </span>)}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
          <PaginationControls
            page={txnPage} pageSize={txnPageSize} total={filteredTxns.length}
            onPage={setTxnPage} onPageSize={(n) => { setTxnPageSize(n); setTxnPage(1); }}
          />
        </div>
      </>)}

      {tab === "exceptions" && (
        <ExceptionsTab onChanged={load} />
      )}

      {tab === "balances" && (
        <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="br-balances-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>{["Bank", "A/c No", "Opening Balance", "+ Credits", "− Debits", "Current Balance", "Txns", "Last Txn"].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {balances.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">No bank ledgers found. Add bank ledgers in Masters first.</td></tr>}
              {balances.map(r => (
                <tr key={r.id || "untagged"} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-2 px-3 font-bold text-slate-900">{r.bank_name || "(Untagged)"}</td>
                  <td className="py-2 px-3 text-slate-500 tabular">****{(r.account_number || "").slice(-4) || "—"}</td>
                  <td className="py-2 px-3 text-right tabular">{formatINR(r.opening_balance)}</td>
                  <td className="py-2 px-3 text-right tabular text-emerald-700 font-bold">+ {formatINR(r.total_credit)}</td>
                  <td className="py-2 px-3 text-right tabular text-rose-700 font-bold">− {formatINR(r.total_debit)}</td>
                  <td className="py-2 px-3 text-right tabular font-bold text-[#F97316]">{formatINR(r.current_balance)}</td>
                  <td className="py-2 px-3 text-right tabular">{r.txn_count}</td>
                  <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{r.last_txn_date ? fmtDate(r.last_txn_date) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "uploads" && (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>{["Uploaded At", "Filename", "Bank", "Txns Inserted", "Skipped (dup)", "Total Debit", "Total Credit", "By"].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {uploads.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-400">No uploads yet.</td></tr>}
              {pagedUploads.map(u => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-2 px-3 text-slate-700 tabular">{(u.uploaded_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="py-2 px-3 font-bold text-slate-900">{u.filename}</td>
                  <td className="py-2 px-3 text-slate-700">{u.bank_ledger_name || "—"}</td>
                  <td className="py-2 px-3 text-right tabular">{u.txn_count}</td>
                  <td className="py-2 px-3 text-right tabular text-amber-700">{u.skipped_duplicates}</td>
                  <td className="py-2 px-3 text-right tabular text-rose-700">{formatINR(u.total_debit)}</td>
                  <td className="py-2 px-3 text-right tabular text-emerald-700">{formatINR(u.total_credit)}</td>
                  <td className="py-2 px-3 text-slate-600">{u.uploaded_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls
            page={uploadsPage} pageSize={uploadsPageSize} total={uploads.length}
            onPage={setUploadsPage} onPageSize={(n) => { setUploadsPageSize(n); setUploadsPage(1); }}
          />
        </div>
      )}

      {uploadOpen && <UploadDialog banks={banks} onClose={() => setUploadOpen(false)} onUploaded={() => { load(); setUploadOpen(false); }}/>}
      {assigning && <AssignBatchDialog txn={assigning} batches={batches} onClose={() => setAssigning(null)} onAssign={assign}/>}
      {raisingException && <ExceptionRequestDialog txn={raisingException}
        onClose={() => setRaisingException(null)}
        onDone={() => { setRaisingException(null); load(); }}/>}
      {bulkAssignOpen && (
        <AssignBatchDialog
          txn={{ value_date: "", amount: Array.from(selected).reduce((s, id) => s + Number((txns.find(t => t.id === id) || {}).amount || 0), 0), description: `Linking ${selected.size} selected transactions` }}
          batches={batches}
          onClose={() => setBulkAssignOpen(false)}
          onAssign={(_, bid) => bulkAssign(bid)}/>
      )}
      {autoOpen && (
        <AutoReconcileDialog
          busy={autoBusy}
          preview={autoPreview}
          onClose={() => { if (!autoBusy) { setAutoOpen(false); setAutoPreview(null); } }}
          onApply={applyAutoReconcile}
          onRefresh={runAutoDryRun}
        />
      )}
      {unlinkOpen && (
        <BulkUnlinkByBatchDialog
          linkedBatches={linkedBatchSummary}
          onClose={() => setUnlinkOpen(false)}
          onUnlink={bulkUnassignByBatch}
        />
      )}
      {reversalPair && (
        <ReversalConfirmDialog pair={reversalPair}
          onClose={() => setReversalPair(null)}
          onConfirm={(remark) => matchReversal(reversalPair.dr.id, reversalPair.cr.id, remark)}
        />
      )}
      {suggestOpen && (
        <ReversalSuggestDialog onClose={() => setSuggestOpen(false)} onMatch={matchReversal} />
      )}
    </div>
  );
}

// 2026-Feb — Auto Reconcile preview + apply modal. All matching logic
// runs server-side; this dialog just renders the response.
function AutoReconcileDialog({ busy, preview, onClose, onApply, onRefresh }) {
  const t = preview || {};
  const matched = t.matched || 0;
  const ambiguous = t.ambiguous || 0;
  const noBatch = t.no_batch_found || 0;
  const scanned = t.scanned_txns || 0;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="br-auto-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50 sticky top-0 z-10">
          <h3 className="font-display text-base font-bold flex items-center gap-2">
            <Wand2 size={16} className="text-emerald-700"/>
            Auto Reconcile — Match Bank Descriptions to Batches
          </h3>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-700 disabled:opacity-40"><X size={16}/></button>
        </div>

        <div className="px-5 py-4">
          {busy && !preview && (
            <div className="py-10 flex flex-col items-center justify-center text-slate-500">
              <Loader2 size={20} className="animate-spin mb-2"/>
              <div className="text-xs">Scanning bank descriptions for batch numbers…</div>
            </div>
          )}

          {preview && (
            <>
              <div className="text-[11px] text-slate-500 mb-3 flex items-start gap-2">
                <Info size={13} className="mt-0.5 text-slate-400 shrink-0"/>
                <span>
                  Scans <strong>unmatched DR</strong> bank transactions and looks for the full batch number (e.g. <code className="font-mono text-[10px] bg-slate-100 px-1">BATCH/0017/26-27</code> → normalised to <code className="font-mono text-[10px] bg-slate-100 px-1">BATCH 0017 26 27</code>) inside each description. When exactly one batch matches, the transaction is linked. Rows with 2+ matching batches are skipped as ambiguous. Amount is not cross-checked. Already-linked and CR (credit) rows are never touched.
                </span>
              </div>

              <div className="grid grid-cols-4 gap-2 mb-4">
                <KpiBadge label="Scanned"       value={scanned}   tone="slate"/>
                <KpiBadge label="Will Link"     value={matched}   tone="emerald"/>
                <KpiBadge label="Ambiguous"     value={ambiguous} tone="amber"/>
                <KpiBadge label="No Batch Found" value={noBatch}  tone="rose"/>
              </div>

              {matched > 0 && (
                <SamplePanel
                  title={`Will link (showing first ${(t.sample_matched || []).length} of ${matched})`}
                  tone="emerald"
                  rows={t.sample_matched || []}
                  columns={[
                    {k: "value_date", label: "Value Date"},
                    {k: "description", label: "Description", truncate: true},
                    {k: "batch_number", label: "→ Batch", strong: true},
                    {k: "amount", label: "Amount", num: true},
                  ]}
                />
              )}
              {ambiguous > 0 && (
                <SamplePanel
                  title={`Ambiguous — skipped (showing first ${(t.sample_ambiguous || []).length} of ${ambiguous})`}
                  tone="amber"
                  rows={t.sample_ambiguous || []}
                  columns={[
                    {k: "description", label: "Description", truncate: true},
                    {k: "candidate_batches", label: "Candidate Batches", join: true},
                    {k: "amount", label: "Amount", num: true},
                  ]}
                />
              )}
              {noBatch > 0 && (
                <SamplePanel
                  title={`No batch number found (showing first ${(t.sample_no_batch || []).length} of ${noBatch})`}
                  tone="rose"
                  rows={t.sample_no_batch || []}
                  columns={[
                    {k: "description", label: "Description", truncate: true},
                    {k: "amount", label: "Amount", num: true},
                  ]}
                />
              )}

              {t.duplicate_batch_keys > 0 && (
                <div className="mt-3 bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900">
                  ⚠︎ {t.duplicate_batch_keys} batch number(s) share a normalised key with another batch — those keys were skipped from the index to avoid accidental mis-links. This is an internal data-integrity note and does not affect the counts above.
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between sticky bottom-0">
          <button onClick={onRefresh} disabled={busy} data-testid="br-auto-refresh"
            className="text-[11px] uppercase font-bold tracking-wider text-slate-600 hover:text-slate-900 disabled:opacity-40">
            Refresh preview
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy}
              className="border border-slate-300 hover:border-slate-500 text-slate-700 px-4 py-2 text-xs uppercase font-bold tracking-wider disabled:opacity-40">
              Cancel
            </button>
            <button onClick={onApply} disabled={busy || !preview || matched === 0} data-testid="br-auto-apply"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs uppercase font-bold tracking-wider flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle2 size={13}/>}
              Apply — Link {matched} txn{matched === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 2026-Feb — Bulk Unlink by Batch companion dialog. Renders a picker
// of only the batches that currently have linked bank txns, shows the
// per-batch count and total amount, and hands off to `onUnlink(batch_id)`
// which calls the new `POST /bank-recon/bulk-unassign-by-batch` endpoint.
function BulkUnlinkByBatchDialog({ linkedBatches, onClose, onUnlink }) {
  const [chosen, setChosen] = useState(linkedBatches[0]?.batch_id || "");
  const chosenRow = linkedBatches.find(b => b.batch_id === chosen);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="br-bulk-unlink-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <h3 className="font-display text-base font-bold flex items-center gap-2">
            <Unlink size={16} className="text-rose-700"/> Bulk Unlink by Batch
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="text-[11px] text-slate-500 flex items-start gap-2">
            <Info size={13} className="mt-0.5 text-slate-400 shrink-0"/>
            <span>
              Choose a batch below. Every bank transaction currently linked to that batch will drop back to <strong>Unmatched</strong>. This is audit-logged. Use this to reverse a wrong Auto Reconcile run in one click.
            </span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Batch</div>
            <select value={chosen} onChange={e => setChosen(e.target.value)}
              data-testid="br-bulk-unlink-picker"
              className="w-full text-xs border border-slate-300 px-2 py-2 bg-white focus:border-rose-600 focus:outline-none">
              <option value="">— Pick a batch —</option>
              {linkedBatches.map(b => (
                <option key={b.batch_id} value={b.batch_id}>
                  {b.batch_number || "(unnamed)"} — {b.count} linked txn{b.count === 1 ? "" : "s"} · ₹{b.total_amount.toLocaleString("en-IN")}
                </option>
              ))}
            </select>
          </div>
          {chosenRow && (
            <div className="bg-rose-50 border border-rose-200 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Preview</div>
              <div className="text-xs text-rose-900 mt-1">
                <strong>{chosenRow.count}</strong> bank transaction(s) totalling <strong>₹{chosenRow.total_amount.toLocaleString("en-IN")}</strong> will be unlinked from <span className="font-mono">{chosenRow.batch_number || "(unnamed)"}</span>.
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose}
            className="border border-slate-300 hover:border-slate-500 text-slate-700 px-4 py-2 text-xs uppercase font-bold tracking-wider">
            Cancel
          </button>
          <button onClick={() => onUnlink(chosen)} disabled={!chosen} data-testid="br-bulk-unlink-confirm"
            className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-xs uppercase font-bold tracking-wider flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Unlink size={13}/> Unlink {chosenRow ? chosenRow.count : 0} txn{chosenRow?.count === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiBadge({ label, value, tone }) {
  const toneMap = {
    slate:   "border-slate-300 bg-slate-50 text-slate-800",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    amber:   "border-amber-300 bg-amber-50 text-amber-800",
    rose:    "border-rose-300 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`border ${toneMap[tone] || toneMap.slate} px-3 py-2 text-center`}>
      <div className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-xl font-bold tabular">{value.toLocaleString("en-IN")}</div>
    </div>
  );
}

function SamplePanel({ title, tone, rows, columns }) {
  const toneMap = {
    emerald: {head: "bg-emerald-50 text-emerald-800", border: "border-emerald-200"},
    amber:   {head: "bg-amber-50 text-amber-800",     border: "border-amber-200"},
    rose:    {head: "bg-rose-50 text-rose-800",       border: "border-rose-200"},
  };
  const c = toneMap[tone] || toneMap.emerald;
  if (!rows || rows.length === 0) return null;
  return (
    <details className={`mb-3 border ${c.border}`} open={tone !== "rose"}>
      <summary className={`px-3 py-2 text-[11px] uppercase font-bold tracking-wider cursor-pointer ${c.head}`}>
        {title}
      </summary>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {columns.map(col => (
                <th key={col.k} className={`text-left py-1.5 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider ${col.num ? "text-right" : ""}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                {columns.map(col => {
                  const val = r[col.k];
                  const shown = col.num
                    ? formatINR(Number(val) || 0)
                    : col.join
                      ? (val || []).join(", ")
                      : (val || "—");
                  return (
                    <td key={col.k} className={`py-1 px-2 ${col.num ? "text-right tabular" : ""} ${col.strong ? "font-mono font-bold text-emerald-800" : "text-slate-700"} ${col.truncate ? "max-w-[420px] truncate" : ""}`}>
                      {shown}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function KPI({ label, value, text = false, accent }) {
  const color = accent === "emerald" ? "text-emerald-700"
    : accent === "amber" ? "text-amber-700"
    : "text-slate-900";
  return (
    <div className="border border-slate-200 bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`text-2xl font-bold tabular ${color}`}>{text ? value : formatINR(value)}</div>
    </div>
  );
}

function UploadDialog({ banks, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [bankId, setBankId] = useState("");
  const [uploading, setUploading] = useState(false);

  async function upload() {
    if (!file) { toast.error("Choose an Excel file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (bankId) fd.append("bank_ledger_id", bankId);
      const r = await api.post("/bank-recon/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.txn_count} transaction(s)${r.data.skipped_duplicates ? ` · ${r.data.skipped_duplicates} duplicates skipped` : ""}`);
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  }
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" onClick={() => !uploading && onClose()}>
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="br-upload-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <h3 className="font-display text-base font-bold flex items-center gap-2"><Upload size={16} className="text-[#F97316]"/> Upload Bank Statement</h3>
          <button onClick={onClose} disabled={uploading} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Bank Statement File (.xls / .xlsx)</div>
            <input type="file" accept=".xls,.xlsx" onChange={e => setFile(e.target.files?.[0])} data-testid="br-upload-file"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 file:mr-2 file:px-3 file:py-1 file:border-0 file:bg-slate-100 file:text-slate-700 file:text-[11px] file:font-bold file:uppercase"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Bank Ledger (optional)</div>
            <select value={bankId} onChange={e => setBankId(e.target.value)} data-testid="br-upload-bank"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 bg-white">
              <option value="">— Not assigned to a specific bank —</option>
              {banks.map(b => <option key={b.id} value={b.id}>{b.bank_name} — ****{(b.account_number || "").slice(-4)}</option>)}
            </select>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 px-3 py-2 text-[11px] text-indigo-900 leading-relaxed">
            Expected columns: Transaction ID, Value Date, Cheque No, Description, Cr/Dr, Transaction Amount, Available Balance. Duplicate transaction IDs from previous uploads are skipped automatically.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} disabled={uploading} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700">Cancel</button>
          <button onClick={upload} disabled={uploading || !file} data-testid="br-upload-submit"
            className="inline-flex items-center gap-1.5 bg-[#F97316] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-50">
            <Upload size={13}/> {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignBatchDialog({ txn, batches, onClose, onAssign }) {
  const [search, setSearch] = useState("");
  const filtered = batches.filter(b => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (b.batch_number || "").toLowerCase().includes(q) ||
           (b.bank_ledger_name || "").toLowerCase().includes(q) ||
           (b.notes || "").toLowerCase().includes(q);
  });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="br-assign-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <h3 className="font-display text-base font-bold flex items-center gap-2"><Link2 size={16} className="text-[#F97316]"/> Link Bank Transaction to ERP Batch</h3>
            <div className="text-[11px] text-slate-500 mt-0.5">{fmtDate(txn.value_date)} · <span className="font-bold">{formatINR(txn.amount)}</span> · {txn.description.slice(0, 60)}{txn.description.length > 60 ? "…" : ""}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} data-testid="br-assign-search"
              placeholder="Search batch number / bank…"
              className="w-full pl-9 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none"/>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {filtered.length === 0 && <div className="py-8 text-center text-slate-400 text-xs">No batches match.</div>}
          {filtered.map(b => (
            <button key={b.id} onClick={() => onAssign(txn.id, b.id)} data-testid={`br-assign-batch-${b.id}`}
              className="w-full text-left border border-slate-200 mb-2 px-3 py-2 hover:bg-orange-50 hover:border-[#F97316] flex items-center justify-between">
              <div>
                <div className="font-bold text-sm text-slate-900">{b.batch_number} <span className="text-[10px] uppercase tracking-wider ml-2 text-slate-500">{b.paid_at ? "Paid" : "Posted"}</span></div>
                <div className="text-[11px] text-slate-500">{fmtDate(b.batch_date)} · {b.bank_ledger_name || "—"} · {b.total_vendors} vendor(s)</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold tabular text-emerald-700">{formatINR(b.total_amount)}</div>
                <div className="text-[10px] text-slate-500">ERP total</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Jul-2026 — Reversal Match (DR ↔ CR offset) dialogs
// ─────────────────────────────────────────────────────────────────────

function ReversalTxnRow({ label, t, tone }) {
  return (
    <div className={`border px-3 py-2 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="flex items-center justify-between gap-3 mt-1">
        <div className="min-w-0">
          <div className="text-xs font-bold text-slate-900 truncate" title={t.description}>{t.description || "—"}</div>
          <div className="text-[10px] text-slate-500">{fmtDate(t.value_date)} · {t.transaction_id || "—"}
            {t.linked_batch_number && <span className="text-rose-600 font-bold"> · linked to {t.linked_batch_number}</span>}
          </div>
        </div>
        <div className="text-sm font-bold tabular whitespace-nowrap">{formatINR(t.amount)}</div>
      </div>
    </div>
  );
}

function ReversalConfirmDialog({ pair, onClose, onConfirm }) {
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="reversal-confirm-dialog">
      <div className="bg-white max-w-lg w-full">
        <div className="px-5 py-3 border-b border-sky-200 bg-sky-50 flex items-center justify-between">
          <div className="text-sm font-bold text-sky-900 inline-flex items-center gap-2"><ArrowLeftRight size={15}/> Match Reversal (DR ↔ CR)</div>
          <button onClick={onClose} className="p-1 hover:bg-sky-100"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-2">
          <ReversalTxnRow label="Payment Debited (DR)" t={pair.dr} tone="border-rose-200 bg-rose-50/40" />
          <ReversalTxnRow label="Refunded by Bank (CR)" t={pair.cr} tone="border-emerald-200 bg-emerald-50/40" />
          {pair.dr.linked_batch_number && (
            <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
              The DR is currently linked to batch <b>{pair.dr.linked_batch_number}</b>. Matching this reversal will
              release that link (audited) so the reprocessed debit can settle the batch instead.
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Reason / Remark (required)</div>
            <textarea data-testid="reversal-remark" rows={2} value={remark} onChange={e => setRemark(e.target.value)}
              placeholder="e.g. NEFT returned — wrong IFSC · account closed · reprocessed on 24-Jul"
              className="w-full border border-slate-300 px-3 py-2 text-xs focus:border-sky-500 focus:outline-none"/>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="border border-slate-300 px-4 py-2 text-xs uppercase font-bold tracking-wider text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="reversal-confirm-btn" disabled={busy || !remark.trim()}
            onClick={async () => { setBusy(true); const ok = await onConfirm(remark.trim()); if (!ok) setBusy(false); }}
            className="bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white px-4 py-2 text-xs uppercase font-bold tracking-wider inline-flex items-center gap-1.5">
            <ArrowLeftRight size={12}/> {busy ? "Matching…" : "Confirm Offset"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReversalSuggestDialog({ onClose, onMatch }) {
  const [pairs, setPairs] = useState(null);
  const [remark, setRemark] = useState("Bank reversal — payment returned and reprocessed");
  const [busyKey, setBusyKey] = useState("");

  async function fetchPairs() {
    setPairs(null);
    try {
      const r = await api.get("/bank-recon/reversal-suggestions");
      setPairs(r.data.pairs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load suggestions");
      setPairs([]);
    }
  }
  useEffect(() => { fetchPairs(); }, []);

  async function doMatch(dr, cr) {
    if (!remark.trim()) { toast.error("Remark is required"); return; }
    setBusyKey(`${dr.id}|${cr.id}`);
    const ok = await onMatch(dr.id, cr.id, remark.trim());
    setBusyKey("");
    if (ok) fetchPairs();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="reversal-suggest-dialog">
      <div className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-sky-200 bg-sky-50 flex items-center justify-between sticky top-0">
          <div className="text-sm font-bold text-sky-900 inline-flex items-center gap-2"><ArrowLeftRight size={15}/> Suggested Reversal Pairs</div>
          <button onClick={onClose} className="p-1 hover:bg-sky-100"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[11px] text-slate-600">
            Each credit (CR) below equals a debit (DR) dated up to 30 days earlier — the classic bounced-payment
            pattern. Confirm only the genuine reversals; matching offsets both rows and releases the DR from its batch.
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Remark applied to each match (required)</div>
            <input data-testid="suggest-remark" value={remark} onChange={e => setRemark(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-xs focus:border-sky-500 focus:outline-none"/>
          </div>
          {pairs === null ? (
            <div className="py-8 text-center text-slate-400 text-xs"><Loader2 size={18} className="animate-spin mx-auto mb-2"/> Scanning for same-amount DR ↔ CR pairs…</div>
          ) : pairs.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-xs">No same-amount DR ↔ CR candidates found. All clear.</div>
          ) : pairs.map((p, pi) => (
            <div key={p.cr.id} className="border border-slate-200" data-testid={`suggest-pair-${pi}`}>
              <div className="px-3 py-2 bg-emerald-50/60 border-b border-slate-200 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Credit back (CR)</div>
                  <div className="text-xs font-bold text-slate-900 truncate" title={p.cr.description}>{p.cr.description || "—"}</div>
                  <div className="text-[10px] text-slate-500">{fmtDate(p.cr.value_date)} · {p.cr.transaction_id || "—"}</div>
                </div>
                <div className="text-sm font-bold tabular text-emerald-700 whitespace-nowrap">{formatINR(p.cr.amount)}</div>
              </div>
              {p.candidates.map(d => (
                <div key={d.id} className="px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-slate-800 truncate" title={d.description}>{d.description || "—"}</div>
                    <div className="text-[10px] text-slate-500">
                      DR · {fmtDate(d.value_date)} · {d.transaction_id || "—"}
                      {d.linked_batch_number && <span className="text-rose-600 font-bold"> · linked to {d.linked_batch_number} (will be released)</span>}
                    </div>
                  </div>
                  <button onClick={() => doMatch(d, p.cr)} disabled={busyKey === `${d.id}|${p.cr.id}`}
                    data-testid={`suggest-match-${pi}-${d.id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider bg-sky-600 text-white px-2.5 py-1 hover:bg-sky-700 disabled:opacity-40">
                    <ArrowLeftRight size={10}/> {busyKey === `${d.id}|${p.cr.id}` ? "Matching…" : "Match"}
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
